import React, { FC, useEffect } from 'react';

import { Box } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { SoftwareCategorySummary } from 'app/api/services/scanVaultSoftware';
import { StackedHeader } from 'components/DataDisplay';
import { Drawer } from 'components/Overlay';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';

import { selectSoftwareInventory } from '../selectors';
import { actions } from '../slice';
import {
  formatSoftwareCount,
  getSoftwareEcosystemIcon,
  getSoftwareTechnologyIcon,
  SoftwareInventoryEntityRow,
} from '../utils';

import { SoftwareInventoryInstallationTable } from './SoftwareInventoryInstallationTable';

interface Props {
  category: SoftwareCategorySummary['category'];
  packageRow: SoftwareInventoryEntityRow;
  isOpen: boolean;
  onClose: () => void;
}

export const SoftwareInventoryInstallationsDrawer: FC<Props> = ({
  category,
  packageRow,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { accountId, environmentId } = useAccessBoundary();
  const { installations } = useSelector(selectSoftwareInventory);

  useEffect(() => {
    if (!isOpen || (!accountId && !environmentId)) return;

    dispatch(
      actions.getSoftwareInstallations({
        q: {
          accountId,
          environmentId,
          category,
          ecosystem: packageRow.ecosystem || undefined,
          // A technology row can span several package names; filter by the
          // product and only pin the name when there is just one.
          ...(packageRow.technologyKey
            ? {
                technology: packageRow.technologyKey,
                name:
                  packageRow.packageNames?.length === 1
                    ? packageRow.packageNames[0]
                    : undefined,
              }
            : { name: packageRow.name }),
          version: packageRow.version || undefined,
          limit: 100,
        },
      }),
    );
  }, [
    accountId,
    category,
    dispatch,
    environmentId,
    isOpen,
    packageRow.ecosystem,
    packageRow.name,
    packageRow.version,
    packageRow.technologyKey,
    packageRow.packageNames,
  ]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      closeButton
      header={
        <StackedHeader
          type={
            packageRow.technologyKey
              ? getSoftwareTechnologyIcon(packageRow.technologyKey)
              : getSoftwareEcosystemIcon(packageRow.ecosystem) || 'Packages'
          }
          upper={
            packageRow.technologyName ||
            packageRow.displayName ||
            packageRow.name ||
            'Unknown package'
          }
          lower={`${formatSoftwareCount(packageRow.hosts)} Entities`}
        />
      }
      body={
        <Box h="full" display="flex" flexDirection="column">
          <Box flex={1} minH={0}>
            <SoftwareInventoryInstallationTable
              rows={installations.data ?? []}
              isLoading={installations.isLoading}
              isError={installations.isError}
            />
          </Box>
        </Box>
      }
      styles={{
        content: { maxW: '1600px' },
        body: { p: 6 },
      }}
    />
  );
};
